"use client"

import { useQuery } from "@tanstack/react-query"
import { dbCharacterToCharacter } from "@/shared/lib/supabase/mappers"
import { queryKeys } from "@/shared/lib/query-keys"
import {
  defaultLocale,
  getCharacterContentLocaleForUiLocale,
  type UiLocale,
} from "@/shared/lib/i18n/config"
import { useCurrentLocale } from "@/shared/hooks/use-current-locale"
import type { Character } from "@/shared/types/character"
import type { LocalizedPublicCharacterRow } from "@/shared/lib/supabase/types"
import { getOptionalSupabasePublishableKey } from "@/shared/lib/supabase/keys"

const SUPABASE_URL = process.env.NEXT_PUBLIC_SUPABASE_URL
const SUPABASE_PUBLISHABLE_KEY = getOptionalSupabasePublishableKey()

// Static ids ("f1") and slugs can never match the RPC's uuid `p_character_id`;
// sending them only produces a 400 (22P02) from Supabase.
const UUID_RE =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i

export async function fetchCharacter(
  id: string,
  locale: UiLocale = defaultLocale
): Promise<Character | null> {
  if (!UUID_RE.test(id)) return null

  try {
    if (!SUPABASE_URL || !SUPABASE_PUBLISHABLE_KEY) return null
    const response = await fetch(
      `${SUPABASE_URL}/rest/v1/rpc/get_localized_public_characters`,
      {
        method: "POST",
        headers: {
          apikey: SUPABASE_PUBLISHABLE_KEY,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          p_locale: getCharacterContentLocaleForUiLocale(locale),
          p_search: null,
          p_gender: null,
          p_style: null,
          p_official: null,
          p_character_id: id,
          p_limit: 1,
          p_offset: 0,
        }),
      }
    )

    if (!response.ok) return null
    const data = (await response.json()) as LocalizedPublicCharacterRow[]
    const row = data[0]
    return row ? dbCharacterToCharacter(row) : null
  } catch {
    return null
  }
}

/**
 * Shared hook to fetch a single public character by ID.
 * Use this from any feature to avoid cross-feature imports.
 *
 * Its key is distinct from the characters feature's `useCharacter`: this
 * fetcher returns `null` for static ids and private rows, which must not be
 * served from cache to the feature hook that can resolve them.
 */
export function useCharacter(id: string) {
  const locale = useCurrentLocale()

  return useQuery({
    queryKey: [...queryKeys.characters.detail(id, locale), "public"] as const,
    queryFn: () => fetchCharacter(id, locale),
    enabled: !!id,
  })
}
